import { CdkDragDrop, DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop';
import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { TranslateModule } from '@ngx-translate/core';

import { SvgIconComponent } from 'src/app/ui/svg-icon/svg-icon.component';

import {
  PRICE_SOURCE_KINDS,
  PriceSourceKind,
} from '../../models/settings.models';

/** One row of the list: a source, whether it is used, and its optional window. */
export interface IPriceSourceRow {
  kind: PriceSourceKind;
  used: boolean;
  freshnessDays: number | null;
}

/**
 * The ordered list of price sources.
 *
 * The order is the policy: the first source that answers wins. So this is a
 * drag-and-drop list and not a set of checkboxes, and the row order is what
 * gets saved.
 *
 * Two rules the screen states rather than offers:
 *
 * * an expired price is never applied automatically. It stays visible and
 *   applicable by hand, and that is a fixed sentence, not a switch — making
 *   it configurable would mean offering a tenant the ability to quote a price
 *   that has lapsed;
 * * a freshness window is optional and empty by default. An empty field means
 *   "no window", never zero: a yearly tariff must not die of a TTL nobody
 *   asked for.
 */
@Component({
  selector: 'app-price-sources-order',
  standalone: true,
  imports: [CommonModule, DragDropModule, FormsModule, MatSlideToggleModule, SvgIconComponent, TranslateModule],
  templateUrl: './price-sources-order.component.html',
  styleUrls: ['./price-sources-order.component.scss'],
})
export class PriceSourcesOrderComponent {
  /** The ordered, used sources. Everything else is listed below, switched off. */
  @Input() set sources(value: PriceSourceKind[]) {
    const used = (value ?? []).filter((kind) => PRICE_SOURCE_KINDS.includes(kind));
    const unused = PRICE_SOURCE_KINDS.filter((kind) => !used.includes(kind));
    this.rows = [
      ...used.map((kind) => this.rowFor(kind, true)),
      ...unused.map((kind) => this.rowFor(kind, false)),
    ];
  }

  @Input() set freshnessDays(value: Partial<Record<PriceSourceKind, number>>) {
    this.windows = { ...(value ?? {}) };
    this.rows = this.rows.map((row) => ({
      ...row,
      freshnessDays: this.windows[row.kind] ?? null,
    }));
  }

  /** Fired on every gesture: reorder, toggle, window. The parent decides what to do. */
  @Output() changed = new EventEmitter<{
    sources: PriceSourceKind[];
    freshnessDays: Partial<Record<PriceSourceKind, number>>;
  }>();

  rows: IPriceSourceRow[] = [];
  private windows: Partial<Record<PriceSourceKind, number>> = {};

  drop(event: CdkDragDrop<IPriceSourceRow[]>): void {
    moveItemInArray(this.rows, event.previousIndex, event.currentIndex);
    this.emit();
  }

  toggle(row: IPriceSourceRow, used: boolean): void {
    row.used = used;
    if (!used) {
      // A source that is switched off disappears from "other known prices"; it
      // is not merely ranked last. Dropping its window with it keeps the saved
      // policy free of settings for sources nobody asked about.
      row.freshnessDays = null;
      delete this.windows[row.kind];
    }
    this.emit();
  }

  setWindow(row: IPriceSourceRow, raw: string): void {
    const value = Number(raw);
    if (!raw || !Number.isFinite(value) || value <= 0) {
      // Empty is "no window". Zero would mean "stale on arrival", which is
      // not a setting this screen offers.
      row.freshnessDays = null;
      delete this.windows[row.kind];
    } else {
      row.freshnessDays = Math.floor(value);
      this.windows[row.kind] = row.freshnessDays;
    }
    this.emit();
  }

  /** Only these two sources can carry a freshness window. */
  windowable(kind: PriceSourceKind): boolean {
    return kind === 'observation' || kind === 'history';
  }

  labelKey(kind: PriceSourceKind): string {
    return `settings.orders_settings.price_source_kind.${kind}`;
  }

  descriptionKey(kind: PriceSourceKind): string {
    return `settings.orders_settings.price_source_kind.${kind}_description`;
  }

  trackByKind(_index: number, row: IPriceSourceRow): string {
    return row.kind;
  }

  private rowFor(kind: PriceSourceKind, used: boolean): IPriceSourceRow {
    return { kind, used, freshnessDays: this.windows[kind] ?? null };
  }

  private emit(): void {
    const used = this.rows.filter((row) => row.used).map((row) => row.kind);
    // Phantom windows die here: a window is only reported for a used source
    // that can carry one, never re-saved for a source that is switched off or
    // cannot go stale.
    const windows: Partial<Record<PriceSourceKind, number>> = {};
    used.filter((kind) => this.windowable(kind)).forEach((kind) => {
      if (this.windows[kind]) {
        windows[kind] = this.windows[kind];
      }
    });
    this.changed.emit({ sources: used, freshnessDays: windows });
  }
}
